import classNames from "classnames";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { MediaImage, MetaobjectField } from "@repo/shopify/types/generated/storefront.types";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { DEFAULT_TITLE } from "@const/meta";

import useMetaObjects from "@queries/useMetaObjects";

import { useLayoutStore } from "@store/layout";

function StoresDetail() {
  const { handle } = useParams();

  const { setMeta } = useLayoutStore();

  const { isPC } = useCheckDevice();

  const { fetchMetaObjectHandle, metaObjectHandleData: data, metaObjectHandleLoading: loading } = useMetaObjects();

  useEffect(() => {
    void fetchMetaObjectHandle({ handle: { handle, type: "store" } });
  }, [handle]);

  useEffect(() => {
    const title = data?.metaobject.fields.find((item) => item.key === "title");
    setMeta({ title: `${title?.value?.replace("\n", " ")} - ${DEFAULT_TITLE}` });
  }, [data]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  const storeData = useMemo(() => {
    const location = JSON.parse(data?.metaobject?.fields.find((item) => item.key === "location")?.value || "[]") as string[];

    return {
      title: data?.metaobject?.fields?.find((item) => item.key === "title")?.value?.replace(/\\n/g, "\n"),
      openingHour: data?.metaobject?.fields?.find((item) => item.key === "opening_hour")?.value?.replace(/\\n/g, "\n"),
      location,
      tel: data?.metaobject?.fields.find((item) => item.key === "tel" || item.key === "phone")?.value || "",
      images: data?.metaobject?.fields.find((item) => item.key === "images")?.references?.edges || [],
    };
  }, [data?.metaobject]);

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <>
          <>
            {isPC ? (
              <section className="sticky top-30 col-span-5 grid h-fit grid-cols-subgrid gap-y-5">
                <h1 className="text-title font-features-normal col-span-5 flex flex-col leading-none whitespace-pre-line">
                  {storeData.title}
                </h1>

                <div className="col-span-5 grid grid-cols-subgrid gap-y-1">
                  <span className="col-span-1">Address</span>
                  <Link as="text" className="col-span-4" href={storeData.location[1]}>
                    {storeData.location[0]}
                  </Link>

                  <span className="col-span-1 leading-none">Phone</span>
                  <Link as="text" className="col-span-4 leading-none" href={`tel:${storeData.tel}`}>
                    {storeData.tel}
                  </Link>

                  <span className="col-span-1">Opening Hours</span>
                  <span className="col-span-4 whitespace-pre-line">{storeData.openingHour}</span>
                </div>
              </section>
            ) : (
              <div className="col-span-8 flex flex-col gap-4">
                <h1 className="text-title font-features-normal flex flex-col leading-none whitespace-pre-line">{storeData.title}</h1>

                <section className="pc:col-span-12 col-span-6 row-start-3 flex flex-col gap-1">
                  <span className="pc:col-span-1 pc:flex col-span-2 hidden">Address</span>
                  <Link as="text" className="pc:col-span-12 col-span-6" href={storeData.location[1]}>
                    {storeData.location[0]}
                  </Link>
                  <span className="pc:col-span-1 pc:flex col-span-2 hidden leading-none">Phone</span>
                  <Link as="text" className="pc:col-span-12 col-span-6 leading-none" href={`tel:${storeData.tel}`}>
                    {storeData.tel}
                  </Link>
                  <span className="pc:col-span-1 pc:flex col-span-2 hidden">Opening Hours</span>
                  <span className="pc:col-span-12 col-span-6 whitespace-pre-line">{storeData.openingHour}</span>
                </section>
              </div>
            )}
          </>

          <section className="pc:col-span-7 pc:pt-0 pc:gap-5 col-span-8 flex flex-col gap-4 pt-7.5">
            {storeData.images.map(({ node }) => (
              <Image
                className="w-full"
                height={(node as MediaImage).image?.height || 2000}
                key={(node as MediaImage).image?.id}
                src={(node as MediaImage).image?.url || ""}
                width={(node as MediaImage).image?.width || 2500}
              />
            ))}
          </section>
        </>
      )}
    </>
  );
}

export default StoresDetail;
